import React from 'react';
import type { Highlight as HighlightData } from '../../types';
import { useReveal } from '../../hooks/useReveal';
import HighlightSpread from './HighlightSpread';
import '../../styles/highlight/Highlight.css';

interface HighlightProps {
  data: HighlightData | null;
}

function plainTitle(t: HighlightData['title']): string {
  return [t.pre, t.accent, t.post].filter(Boolean).join('');
}

/**
 * Single-highlight surface — renders one spread inside its own desk section.
 * Public API kept intact (R1+R2); `<HighlightDeck />` composes
 * `<HighlightSpread />` directly when multiple highlights are active.
 */
const Highlight: React.FC<HighlightProps> = ({ data }) => {
  const { ref, revealed } = useReveal<HTMLElement>();

  if (!data) return null;

  const title = plainTitle(data.title);
  const ariaLabel = `${data.chapterMark} — ${title}`;

  return (
    <section
      ref={ref}
      aria-label={ariaLabel}
      className={`psl-hl-desk psl-reveal${revealed ? ' is-in-view' : ''}`}
    >
      <HighlightSpread data={data} ariaTitle={title} />
    </section>
  );
};

export default Highlight;
